<template>
  <div class="modal fade" tabindex="-1" id="professional_onboarding_modal" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
      <div class="modal-content onboarding-modal">
        <div class="modal-header border-0 pb-0">
          <div>
            <span class="badge badge-light-primary mb-3">Configuração inicial</span>
            <h3 class="modal-title">Vamos preparar sua clínica para atender</h3>
            <p class="text-muted mb-0">Defina o funcionamento básico da agenda. Você poderá alterar tudo depois.</p>
          </div>
        </div>

        <div class="modal-body">
          <div class="onboarding-summary">
            <div>
              <i class="bi bi-calendar2-week"></i>
              <strong>Horários de atendimento</strong>
              <span>Dias e períodos em que sua agenda ficará disponível.</span>
            </div>
            <div>
              <i class="bi bi-clock-history"></i>
              <strong>Duração e retorno</strong>
              <span>Tempo médio da consulta e prazo para retornos.</span>
            </div>
            <div>
              <i class="bi bi-camera-video"></i>
              <strong>Modalidade</strong>
              <span>Online, presencial ou ambos por padrão.</span>
            </div>
          </div>

          <div class="row g-4 mt-2">
            <div class="col-md-4">
              <label class="form-label required">Tempo médio</label>
              <div class="input-group input-group-solid">
                <input type="number" class="form-control" min="5" max="240" v-model.number="form.interval_minutes">
                <span class="input-group-text">min</span>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label required">Liberação padrão</label>
              <select class="form-control form-control-solid" v-model="form.default_release_time_for">
                <option value="B">Online e presencial</option>
                <option value="F">Presencial</option>
                <option value="O">Online</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Prazo de retorno</label>
              <div class="input-group input-group-solid">
                <input type="number" class="form-control" min="0" max="365" v-model.number="form.returning_deadline">
                <span class="input-group-text">dias</span>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-8 mb-4">
            <div>
              <h5 class="mb-1">Dias e horários</h5>
              <small class="text-muted">Começamos com segunda a sexta em horário comercial.</small>
            </div>
            <button type="button" class="btn btn-light-primary btn-sm" @click="copyMondayToBusinessDays">
              <i class="bi bi-files"></i>
              Copiar segunda para dias úteis
            </button>
          </div>

          <div class="onboarding-days">
            <OnboardingWeekdayRow
              v-for="(day, index) in form.weekly_ranges"
              :key="day.week_day"
              :day="day"
              @change="updateDay(index, $event)"
            />
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-primary btn-lg" type="button" :disabled="saving" @click="submit">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            Salvar e começar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from '@/helpers/alert';
import http from '@/helpers/http';
import OnboardingWeekdayRow from './OnboardingWeekdayRow.vue';

const defaultRanges = () => [
  { time_begin: '08:00', time_end: '12:00' },
  { time_begin: '13:00', time_end: '18:00' },
];

export default {
  name: 'ProfessionalOnboardingModal',
  components: { OnboardingWeekdayRow },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saving: false,
      form: {
        interval_minutes: 30,
        default_release_time_for: 'B',
        returning_deadline: 30,
        weekly_ranges: [
          { week_day: 1, name: 'Segunda', enabled: true, ranges: defaultRanges() },
          { week_day: 2, name: 'Terça', enabled: true, ranges: defaultRanges() },
          { week_day: 3, name: 'Quarta', enabled: true, ranges: defaultRanges() },
          { week_day: 4, name: 'Quinta', enabled: true, ranges: defaultRanges() },
          { week_day: 5, name: 'Sexta', enabled: true, ranges: defaultRanges() },
          { week_day: 6, name: 'Sábado', enabled: false, ranges: defaultRanges() },
          { week_day: 7, name: 'Domingo', enabled: false, ranges: defaultRanges() },
        ],
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    professional() {
      return this.$store.state.auth.professionals?.[0];
    },
  },
  mounted() {
    if (!this.showModal) return;

    this.$store.dispatch('auth/loadProfessionals').then(() => {
      bootstrap.Modal.getOrCreateInstance(document.getElementById('professional_onboarding_modal')).show();
    });
  },
  methods: {
    updateDay(index, day) {
      this.form.weekly_ranges.splice(index, 1, day);
    },
    copyMondayToBusinessDays() {
      const monday = this.form.weekly_ranges[0];
      this.form.weekly_ranges = this.form.weekly_ranges.map((day) => {
        return day.week_day > 5 ? day : { ...day, enabled: true, ranges: monday.ranges.map((range) => ({ ...range })) };
      });
    },
    submit() {
      if (!this.professional?.id_prof) {
        Alert.toastError('Não foi possível identificar o profissional.');
        return;
      }

      this.saving = true;
      http.post(`/places/${this.place.id_place}/professional-onboarding`, {
        ...this.form,
        id_prof: this.professional.id_prof,
      }).then((response) => {
        this.$store.commit('auth/setPlace', response.data);
        bootstrap.Modal.getOrCreateInstance(document.getElementById('professional_onboarding_modal')).hide();
        Alert.toastSuccess('Agenda configurada com sucesso.');

        this.$store.dispatch('auth/refreshEntitlements').catch(() => {});
        this.$store.dispatch('auth/loadProfessionals').catch(() => {});
      }).catch((error) => {
        Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Não foi possível salvar o onboarding.');
      }).finally(() => {
        this.saving = false;
      });
    },
  },
};
</script>

<style scoped>
.onboarding-modal {
  border: 0;
  border-radius: 22px;
}

.onboarding-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.onboarding-summary > div {
  display: grid;
  gap: 0.35rem;
  padding: 1rem;
  border-radius: 16px;
  background: #f8f5fb;
}

.onboarding-summary i {
  color: var(--bs-primary);
  font-size: 1.35rem;
}

.onboarding-days {
  display: grid;
  gap: 0.75rem;
}

@media (max-width: 767px) {
  .onboarding-summary {
    grid-template-columns: 1fr;
  }
}
</style>
